import { useLocation } from 'react-router-dom';
import type { SidebarConfig } from './types';
import { financeSidebar } from './financeSidebar';
import { engineeringSidebar } from './engineeringSidebar';
import { operationsSidebar } from './operationsSidebar';
import { contractsSidebar } from './contractsSidebar';
import { hrSidebar } from './hrSidebar';
import { marketingSidebar } from './marketingSidebar';
import { legalSidebar } from './legalSidebar';
import { workflowsSidebar } from './workflowsSidebar';
import { settingsSidebar } from './settingsSidebar';

/**
 * Registry of every module sidebar, keyed by the first URL segment.
 *
 * Keep this map narrow — only modules whose deep navigation deserves a
 * dedicated sidebar belong here. Routes outside this map (Landing, About,
 * Dashboard) render with no module sidebar; the primary rail is enough.
 */
const REGISTRY: Record<string, SidebarConfig> = {
  finance: financeSidebar,
  engineering: engineeringSidebar,
  operations: operationsSidebar,
  contracts: contractsSidebar,
  hr: hrSidebar,
  marketing: marketingSidebar,
  legal: legalSidebar,
  workflows: workflowsSidebar,
  settings: settingsSidebar,
};

/**
 * Resolve the active module sidebar config for the current route.
 * Returns `null` for paths that don't have a module sidebar (Landing, Dashboard,
 * About, etc.) — the layout collapses the sub-sidebar away in that case.
 */
export function useModuleSidebar(): SidebarConfig | null {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const moduleKey = segments[0];
  if (!moduleKey) return null;
  return REGISTRY[moduleKey] ?? null;
}

/** Exposed for the primary rail so it can highlight the active module icon. */
export function activeModuleKey(pathname: string): string | null {
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ?? null;
}
